import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isLoaded, useFirestoreConnect } from 'react-redux-firebase';
import EnhancedTable from './EnhancedTable/EnhancedTable';
import { useIntl } from 'react-intl';
import { getDomainSettingsPath, getSessionsPath } from '../../util/helpers/path';
import { useTableDataParser } from '../../workers/dataWorkerApi';
import { Checkbox, useTheme, Switch } from '@material-ui/core';
import { fetchStoragePicture } from '../../redux/actions/userActions';
import { getFirst } from '../../util/helpers/other';
import { getStoredOrDefault, storeCheckState } from '../../util/web';

const showInputDataStorageKey = 'sessionShowInputData';
const showBaseFieldsStorageKey = 'sessionShowBaseFields';
const mergeLabelsStorageKey = 'sessionMergeLabels';

export default function Session(props) {
  const { match } = props;
  const domain = match.params.domain;
  const session = match.params.session;

  const intl = useIntl();
  const theme = useTheme();
  const dispatch = useDispatch();

  const customSettingsKey = session ? `sett${domain}-${session}` : null;

  const { profile, sessions, defaultSettings, customSettings, sessionData } = useSelector(
    ({ firebase: { profile }, firestore: { data } }) => {
      const sessions = data[`sess${domain}`];
      let sessionData = (isLoaded(sessions) && sessions && sessions[session]) || null;

      return {
        profile,
        sessions,
        sessionData,
        defaultSettings: data[`sett${domain}`],
        customSettings: customSettingsKey && sessionData ? data[customSettingsKey] : null,
      };
    }
  );

  const settings = customSettings ?? defaultSettings; // ->

  const queries = useMemo(() => {
    if (isLoaded(profile) && profile) {
      // Sessions query
      const _queries = [
        {
          collection: getSessionsPath(profile.company, domain),
          storeAs: `sess${domain}`,
          orderBy: ['startTime', 'desc'],
          limit: 100,
        },
      ];
      if (session && sessionData && profile) {
        // Custom settings query
        _queries.push({
          collection: getDomainSettingsPath(profile.company, domain),
          where: [['createdDate', '<', sessionData.startTime]],
          orderBy: ['createdDate', 'desc'],
          limit: 1,
          storeAs: customSettingsKey,
        });
      }
      return _queries;
    }
    return queries ?? [];
  }, [domain, session, profile, sessionData, customSettingsKey]);
  useFirestoreConnect(queries);

  const company = profile && profile.company;
  let wasTask = (sessionData && sessionData.wasTask) || false;

  const [showInputData, setShowInputData] = useState(wasTask && getStoredOrDefault(showInputDataStorageKey, false));
  const [showBaseFields, setShowBaseFields] = useState(getStoredOrDefault(showBaseFieldsStorageKey, false));
  const [mergeLabels, setMergeLabels] = useState(getStoredOrDefault(mergeLabelsStorageKey, false));

  const sett = settings && getFirst(settings);
  const { isWorking, rowsAndColumns } = useTableDataParser(
    sessionData,
    sett,
    showInputData,
    showBaseFields,
    mergeLabels
  );

  const { rows, columns } = rowsAndColumns || {};

  const topToolbarExtraSelectors = [];
  if (wasTask) {
    topToolbarExtraSelectors.push({
      label: intl.formatMessage({ id: 'session.inputFormat' }),
      control: (
        <Checkbox
          style={{ marginEnd: theme.spacing(2) }}
          color="secondary"
          checked={showInputData}
          onChange={storeCheckState(showInputDataStorageKey, setShowInputData)}
        />
      ),
    });
  }

  topToolbarExtraSelectors.push({
    label: intl.formatMessage({ id: 'session.showBaseFields' }),
    control: (
      <Checkbox
        style={{ marginEnd: theme.spacing(2) }}
        color="secondary"
        checked={showBaseFields}
        onChange={storeCheckState(showBaseFieldsStorageKey, setShowBaseFields)}
      />
    ),
  });

  const bottomToolbarExtraSelectors = [
    {
      label: intl.formatMessage({ id: 'session.mergeLabels' }),
      control: <Switch checked={mergeLabels} onChange={storeCheckState(mergeLabelsStorageKey, setMergeLabels)} />,
    },
  ];

  const dataLoaded = isLoaded(settings, sessions) && !isWorking;
  const addressField = sett && sett.baseFields.address;

  return (
    <EnhancedTable
      tableId="session"
      dataLoaded={dataLoaded}
      data={{ rows, columns }}
      title={intl.formatMessage({ id: 'domain.title' }, { domain: match.params.domain })}
      showTitle
      showPicture
      onPictureClick={(row) => {
        dispatch(
          fetchStoragePicture(
            company,
            domain,
            row.address,
            row.checkedAt.getTime(),
            row.__pictureFilename,
            addressField,
            intl
          )
        );
      }}
      toolbarProps={{
        exportButton: true,
        exportFileName: match.params.session,
        title: intl.formatMessage({ id: 'session.title' }, { session: match.params.session }),
        extraSelectors: topToolbarExtraSelectors,
      }}
      bottomToolbarProps={{
        extraSelectors: bottomToolbarExtraSelectors,
      }}
    />
  );
}
